import { create } from 'zustand';
import { request } from '../utils';
import { UNIFIED_STATUS, WEBHOOK_STATUS } from '../utils/constant';
import { PaymentDetails } from '../components/PaymentLink/types';
import { WALLET_STATUS, WalletTransactionType } from '../components/wallet/types';

export type WebhookTransactionType = 'payment_link' | 'exchange' | 'wallet_creation' | 'wallet_txn' | 'stable_wallet_creation' | 'stable_wallet_txn' | 'wallet_virtual_account' | 'unknown';

export interface Webhook {
  _id: string;
  businessId: string;
  webhookUrl: string;
  status: WEBHOOK_STATUS;
  data: {
    globalId: string;
    offerId?: string;
    orderId?: string;
    bdcId?: string;
    buyerGlobalId?: string;
    sellerGlobalId?: string;
    userId?: string;
    userGlobalId?: string;
    fromCurrency?: string;
    toCurrency?: string;
    rate?: number;
    fee?: number;
    bdcFee?: number;
    amount: number;
    status: UNIFIED_STATUS | WALLET_STATUS;
    buyerDebitPaymentMethod?: string;
    buyerCreditPaymentMethod?: string;

    // ---- buyer/seller payment details
    buyerCreditPaymentDetails?: PaymentDetails;
    buyerDebitPaymentDetails?: PaymentDetails;
    sellerCreditPaymentDetails?: PaymentDetails;

    // ---- payment link–specific fields
    paymentDetails?: PaymentDetails;
    payerDetails?: {
      name: string;
      email: string;
    };
    currencyIso2?: string;
    displayMessage?: string;
    expiresAt?: string;
    token?: string;

    source?: string;
    buyerMadePayment?: boolean;
    hasBuyerPaymentProof?: boolean;
    buyerPaymentProofUrl?: string | null;
    sellerMadePayment?: boolean;
    sellerReceivedPayment?: boolean;
    paymentReferenceCode?: number;

    _id?: string;
    createdAt: string;
    updatedAt: string;
    __v?: number;

    // ---- category
    category?: WebhookTransactionType | WalletTransactionType;

    // ---- wallet txn specific
    paymentMethod: string;
    reference?: string;
    isReversed?: boolean;
    purpose?: string;
    narration?: string;
    paymentGatewayReference?: string;
    valueGiven?: boolean;
    type?: string;
    metadata?: {
      senderAccountNumber?: string;
      senderName?: string;
      sender?: any;
      receiver?: {
        phoneNumber?: string;
        network?: string;
      } | null;
    };
    name?: string;
    symbol?: string;
    flag?: string;

    // wallet_creation specific
    incomingBalance?: number;
    hasBankAccount?: boolean;
    id?: string;
    currency?: string;
    balance?: number;
    heldBalance?: number;

    // virtual account specific
    accountName?: string;
    accountNumber?: string;
    bankCode?: string;
    bankName?: string;
    accountRef?: string;
    externalReference?: string;
    validFor?: number;
    expiryDate?: string;

    // stable currency specific
    walletId?: string;
    txHash?: string;
    from?: string;
    to?: string;
    network?: string;
    address?: string;
    solanaAddress?: string;
    tronAddress?: string;
    parentId?: string;


  };
  trialCount: number;
  lastTriedAt: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}


interface WebhooksState {
  webhooks: Webhook[];
  filteredWebhooks: Webhook[];
  loading: boolean;
  error: string | null;
  hasInitialLoad: boolean;
  loadWebhooks: () => Promise<void>;
  setFilteredWebhooks: (webhooks: Webhook[]) => void;
  retryWebhook: (webhookId: string) => Promise<boolean>;
  updateWebhook: (webhookId: string, updates: Partial<Webhook>) => void;
}

export const useWebhookStore = create<WebhooksState>((set, get) => ({
  webhooks: [],
  filteredWebhooks: [],
  loading: true,
  error: null,
  hasInitialLoad: false,

  loadWebhooks: async () => {
    set({ loading: true });
    try {
      const response = await request.get({
        url: "/webhooks",
      });
      const webhooksData = Array.isArray(response.data)
        ? (response.data as Webhook[])
        : [];

      const sortedWebhooks = webhooksData.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      set({
        webhooks: sortedWebhooks,
        filteredWebhooks: sortedWebhooks,
        hasInitialLoad: true,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      set({ 
        error: error.message || "Failed to load webhooks",
        loading: false 
      });
      console.error("Failed to load webhooks:", error);
    }
  },

  setFilteredWebhooks: (filteredWebhooks: Webhook[]) => {
    set({ filteredWebhooks });
  },

 retryWebhook: async (webhookId: string) => {
  try {
    const response = await request.post({
      url: `/webhook/${webhookId}/retry`,
    });

    if (response.success) {
      const webhookResponse = await request.get({
        url: `/webhook/${webhookId}`,
      });

      const webhookData = webhookResponse as {
        success: boolean;
        message: string;
        data: Webhook;
      };

      const updatedWebhook = webhookData.data;
      const { updateWebhook } = get();
      updateWebhook(webhookId, updatedWebhook);
      return true;
    } else {
      set({ error: response.message });
      setTimeout(() => set({ error: null }), 3000);
      throw new Error(response.message || "Failed to retry webhook");
    }
    
  } catch (error: any) {
    console.error("Failed to retry webhook:", error);
    set({ error: error.message });
    const { updateWebhook, webhooks } = get();
      const currentWebhook = webhooks.find(
        (webhook) => webhook._id === webhookId
      );

      if (currentWebhook) {
        updateWebhook(webhookId, {
          trialCount: (currentWebhook.trialCount || 0) + 1,
          lastTriedAt: new Date().toISOString(),
        });
      }

    setTimeout(() => set({ error: null }), 3000);
    throw new Error(error.message || "Failed to retry webhook");
  }
},


  updateWebhook: (webhookId: string, updates: Partial<Webhook>) => {
    const { webhooks, filteredWebhooks } = get();

    const updatedWebhooks = webhooks.map(webhook =>
      webhook._id === webhookId ? { ...webhook, ...updates } : webhook
    );

    const updatedFilteredWebhooks = filteredWebhooks.map(webhook =>
      webhook._id === webhookId ? { ...webhook, ...updates } : webhook
    );

    set({
      webhooks: updatedWebhooks,
      filteredWebhooks: updatedFilteredWebhooks,
    });
  },
}));